import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import { Button } from "@material-ui/core";
import dateFormat from "dateformat";
import ListPagination from "../../components/ListPagination/ListPagination";

const useStyles = makeStyles({
	table: {
		"& tbody": {
			"& th,td": {
				fontWeight: 500,
			},
		},
	},
	filterRow: {
		display: "flex",
		gap: "12px",
		alignItems: "center",
		marginBottom: "16px",
		flexWrap: "wrap",
	},
});

const formatSource = (source) => {
	if (source === "round-complete") return "Round Complete";
	if (source === "header") return "Header";
	return "Other";
};

const escapeCsv = (value) => {
	const str = value === undefined || value === null ? "" : String(value);
	if (/[",\n]/.test(str)) {
		return `"${str.replace(/"/g, '""')}"`;
	}
	return str;
};

const RegularsTable = ({
	entries,
	rooms,
	total,
	page,
	limit,
	selectedRoom,
	emailFilter,
	onRoomChange,
	onEmailChange,
	onRefresh,
	onPageChange,
	onLimitChange,
	onExportCsv,
}) => {
	const classes = useStyles();

	const roomCodes = useMemo(() => {
		return (rooms || []).map((room) => room.roomCode).filter(Boolean).sort();
	}, [rooms]);

	const exportCsv = async () => {
		const exportEntries = await onExportCsv();
		if (!exportEntries.length) return;

		const header = [
			"Email",
			"Team Name",
			"Score",
			"Round",
			"Room Code",
			"Source",
			"Date",
		];
		const rows = exportEntries.map((entry) => [
			entry.email,
			entry.teamName,
			entry.score,
			entry.roundName,
			entry.roomCode,
			entry.source,
			dateFormat(entry.createdAt, "mmm d, yyyy h:MM TT"),
		]);
		const csv = [header, ...rows]
			.map((row) => row.map(escapeCsv).join(","))
			.join("\n");
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		const suffix = selectedRoom || "all-rooms";
		link.href = url;
		link.setAttribute("download", `regulars-${suffix}.csv`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	return (
		<>
			<div className={classes.filterRow}>
				<Select
					value={selectedRoom}
					onChange={(event) => onRoomChange(event.target.value)}
					displayEmpty
					style={{ minWidth: 180 }}
				>
					<MenuItem value="">All Rooms</MenuItem>
					{roomCodes.map((roomCode) => (
						<MenuItem key={roomCode} value={roomCode}>
							{roomCode}
						</MenuItem>
					))}
				</Select>

				<TextField
					label="Filter by email"
					variant="outlined"
					size="small"
					value={emailFilter}
					onChange={(event) => onEmailChange(event.target.value)}
					style={{ minWidth: 220 }}
				/>

				<Button variant="outlined" color="primary" onClick={onRefresh}>
					Refresh
				</Button>

				<Button
					variant="outlined"
					color="primary"
					onClick={exportCsv}
					disabled={!total}
				>
					Export CSV
				</Button>
			</div>

			<TableContainer>
				<Table className={classes.table} aria-label="regulars table">
					<TableHead>
						<TableRow>
							<TableCell>Email</TableCell>
							<TableCell>Team Name</TableCell>
							<TableCell>Score</TableCell>
							<TableCell>Round</TableCell>
							<TableCell>Room Code</TableCell>
							<TableCell>Source</TableCell>
							<TableCell>Date</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{entries.length === 0 ? (
							<TableRow>
								<TableCell colSpan={7} align="center">
									No regulars match the current filters.
								</TableCell>
							</TableRow>
						) : (
							entries.map((entry) => (
								<TableRow key={entry._id}>
									<TableCell>{entry.email}</TableCell>
									<TableCell>{entry.teamName || "—"}</TableCell>
									<TableCell>{entry.score}</TableCell>
									<TableCell>{entry.roundName || "—"}</TableCell>
									<TableCell>{entry.roomCode || "—"}</TableCell>
									<TableCell>{formatSource(entry.source)}</TableCell>
									<TableCell>
										{entry.createdAt
											? dateFormat(entry.createdAt, "mmm d, yyyy h:MM TT")
											: "—"}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<ListPagination
				total={total}
				page={page}
				limit={limit}
				onPageChange={onPageChange}
				onLimitChange={onLimitChange}
			/>
		</>
	);
};

export default RegularsTable;
